import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getVisitorProperty,
  getVisitorUnits,
  getVisitorSettings,
  VisitorProperty,
  VisitorUnit,
  VisitorSettings,
} from '../../api/visitor';
import { trackVisitorView } from '../../utils/analytics';
import { apiOrigin, IS_VISITOR_SURFACE } from '../../utils/surface';
import { showVisitorFaceEntry, showVisitorPinEntry } from '../../utils/intercomEntryPolicy';
import { DeviceActionButton } from '../../components/access/DeviceVisuals';

type ViewMode = 'directory' | 'search';

const visitorBase = () => IS_VISITOR_SURFACE ? '' : '/visit';

export const VisitorDirectory: React.FC = () => {
  const { propertyId, intercomId } = useParams();
  const navigate = useNavigate();
  // When the visitor came in via /:propertyId/i/:intercomId we keep the
  // intercom segment in every downstream URL so the call screen knows which
  // doors it's allowed to unlock.
  const intercomPathSegment = intercomId ? `/i/${intercomId}` : '';
  // Kiosk mode (`?kiosk=1`): the device IS the intercom panel, so we hide
  // every `tel:` button (the kiosk can't dial out — it'd just open an
  // external browser handler) and force all calls through the in-app
  // LiveKit video path. Set automatically by the Pi's start-kiosk.sh.
  const isKioskMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('kiosk') === '1';

  const [property, setProperty] = useState<VisitorProperty | null>(null);
  const [units, setUnits] = useState<VisitorUnit[]>([]);
  const [settings, setSettings] = useState<VisitorSettings | null>(null);
  // When the URL is /:propertyId/i/:intercomId we look up the intercom so its
  // label can be shown beneath the property name as an "opening name", and
  // its phone_number gates whether the phone-call button is rendered. A
  // video-only intercom (no phone) hides the phone path entirely.
  const [intercomLabel, setIntercomLabel] = useState<string>('');
  const [intercomPhone, setIntercomPhone] = useState<string | null>(null);
  const [hiddenUnitIds, setHiddenUnitIds] = useState<Set<string>>(new Set());
  // Per-intercom unlock-method overrides — null = no override, fall back to property
  // null = property-level fallback; '' = explicitly video-only.
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('directory');
  const [showPinPad, setShowPinPad] = useState(false);
  const [pin, setPin] = useState('');
  const [pinStatus, setPinStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [pinMessage, setPinMessage] = useState('');
  // When the redeemed PIN is a delivery code we surface the tenant's
  // instructions on a full-screen overlay so the courier sees them.
  const [deliveryInfo, setDeliveryInfo] = useState<{
    carrier: string;
    notes: string;
    tenantName: string;
    doorMessage: string;
  } | null>(null);

  // Face ID state
  const FACE_API = apiOrigin();
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [faceStatus, setFaceStatus] = useState<'idle' | 'starting' | 'scanning' | 'liveness' | 'capturing' | 'verifying' | 'success' | 'error'>('idle');
  const [faceMessage, setFaceMessage] = useState('');
  const [faceCountdown, setFaceCountdown] = useState(3);
  const [livenessProgress, setLivenessProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const livenessTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const captureAndVerifyRef = useRef<() => void>(() => {});

  useEffect(() => {
    loadData();
    // Re-load when the intercom segment changes too so the opening name
    // updates without a full page reload.
  }, [propertyId, intercomId]);

  // A directory may remain open on a kiosk or visitor's phone while the PM
  // changes an intercom setting. Refresh visible pages without a full reload.
  useEffect(() => {
    if (!propertyId || !intercomId) return;
    const refresh = () => { if (document.visibilityState === 'visible') void loadData(true); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [propertyId, intercomId]);

  // Visitor-side analytics. Fires a `page_view` once per (property, intercom)
  // mount and a `page_duration` follow-up on unmount/route-change so each
  // intercom's directory accrues its own views and dwell-time.
  useEffect(() => {
    if (!propertyId) return;
    return trackVisitorView({
      property_id: propertyId,
      intercom_config_id: intercomId ?? null,
      metadata: { surface: 'visitor_directory' },
    });
  }, [propertyId, intercomId]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {
      let prop: VisitorProperty;
      if (propertyId) {
        prop = await getVisitorProperty(propertyId);
      } else {
        setError('No property specified.');
        if (!silent) setLoading(false);
        return;
      }

      // If accessed with numeric ID but property has a UUID, redirect to UUID URL
      const propUuid = (prop as any).property_uuid;
      if (propertyId && !propertyId.includes('-') && propUuid) {
        const basePath = IS_VISITOR_SURFACE ? '' : '/visit';
        window.history.replaceState(null, '', `${basePath}/${propUuid}`);
      }

      setProperty(prop);

      const [unitData, settingsData] = await Promise.all([
        getVisitorUnits(prop.id),
        getVisitorSettings(prop.id, intercomId),
      ]);

      setUnits(unitData);
      setSettings(settingsData);

      // Resolve the intercom label + phone (if visiting via /i/:intercomId).
      // Label renders under the property name as an opening greeting; phone
      // (or the absence of one) gates the visitor's phone-call button.
      if (intercomId) {
        try {
          const API = (window as any).REACT_APP_API_URL || apiOrigin();
          const res = await fetch(`${API}/public/intercom-configs?property_id=${encodeURIComponent(String(prop.id))}`, { cache: 'no-store' });
          if (res.ok) {
            const list = await res.json();
            const match = (Array.isArray(list) ? list : []).find(
              (c: any) => String(c.id) === String(intercomId) || String(c.uuid) === String(intercomId),
            );
            if (!match || match.enabled === false || match.intercom_enabled === false) {
              setError('This intercom directory is not available.');
              return;
            }
            if (match) {
              const savedLabel = (match.label || match.name || '').toString().trim();
              if (savedLabel) setIntercomLabel(savedLabel);
              const ph = (match.phone_number || '').toString().trim();
              setIntercomPhone(ph || ''); // '' marks video-only explicitly
              const hid = Array.isArray(match.hidden_unit_ids)
                ? match.hidden_unit_ids
                : (typeof match.hidden_unit_ids === 'string'
                  ? (() => { try { return JSON.parse(match.hidden_unit_ids); } catch { return []; } })()
                  : []);
              setHiddenUnitIds(new Set(hid.map(String)));
              setIntercomLoaded(true);
            }
          }
        } catch {/* non-fatal — header just falls back to property name */}
      } else {
        setIntercomLabel('');
        setIntercomPhone(null);
        setIntercomLoaded(false);
        // No intercom in URL → resolve the property's first enabled
        // intercom and redirect there. Every intercom has its own
        // greeting / voice / theme, so the visitor URL must be
        // intercom-scoped for the right settings to render. The
        // redirect replaces history so the picker URL doesn't sit in
        // the back stack.
        try {
          const API = (window as any).REACT_APP_API_URL || apiOrigin();
          const res = await fetch(`${API}/public/intercom-configs?property_id=${encodeURIComponent(String(prop.id))}`);
          if (res.ok) {
            const list = await res.json();
            const arr = Array.isArray(list) ? list : [];
            const first = arr.find((c: any) => c.enabled !== false);
            const firstId = first && String(first.uuid || first.intercom_config_uuid || first.id || '');
            if (firstId) {
              navigate(`${visitorBase()}/${propertyId}/i/${firstId}`, { replace: true });
              return;
            }
          }
        } catch {/* fall through to property-level rendering */}
      }
    } catch (err: any) {
      console.error('Failed to load visitor data:', err);
      if (!silent) setError('Property not found or visitor access is not enabled.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ─── Filter units by search ───────────────────────────────────────────────

  const filteredUnits = units
    // Per-intercom hide list: PM can hide specific units from this intercom's
    // visitor directory. Only enforced when we resolved an intercom_config —
    // bare /:propertyId visitor pages still see every unit.
    .filter((u: any) => !intercomLoaded || !hiddenUnitIds.has(String(u.id)))
    // The canonical units response may include occupants in `tenants` but no
    // legacy `tenant_name`. Do not hide an occupied unit in that case.
    .filter((u: any) => !settings?.hide_vacant_units || !!u.tenant_name || !!u.tenants?.length)
    .filter((u) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        u.unit_number.toLowerCase().includes(q) ||
        (u.display_name && u.display_name.toLowerCase().includes(q)) ||
        (u.tenant_name && u.tenant_name.toLowerCase().includes(q)) ||
        (u.floor && u.floor.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      const order = settings?.unit_sort_order || 'Numerical';
      if (order === 'Alphabetical') return String(a.unit_number).localeCompare(String(b.unit_number));
      // 'Numerical' (default) — natural-numeric sort.
      return String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true });
    });

  // ─── Group units by floor ─────────────────────────────────────────────────

  const unitsByFloor = filteredUnits.reduce<Record<string, VisitorUnit[]>>((acc, u) => {
    const floor = u.floor || 'Other';
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(u);
    return acc;
  }, {});

  const floors = Object.keys(unitsByFloor).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

  // ─── Get display label for a unit ─────────────────────────────────────────

  const getUnitLabel = (unit: VisitorUnit): string | null => {
    const mode = settings?.directory_display || property?.directory_display || 'tenant_name';
    if (mode === 'unit_only') return null;
    if (mode === 'display_name') return unit.display_name || null;
    return unit.tenant_name || unit.display_name || null;
  };

  // Each tenant gets their own card — expand a unit into one entry per
  // occupant. If a unit has no tenants (vacant or directory_display !=
  // tenant_name), fall back to a single unit-only entry. Subtenants were
  // already filtered server-side based on the property's
  // subtenant_receive_calls setting.
  type DirEntry = {
    key: string;
    unit: VisitorUnit;
    label: string | null;
    tenantId?: string;
    /// True when this entry can receive an Intrlock video call. Mobile tenants
    /// require activation; property desk users route to Desk Station and are
    /// callable without a mobile activation.
    tenantActivated: boolean;
  };
  const expandUnit = (unit: VisitorUnit): DirEntry[] => {
    const mode = settings?.directory_display || property?.directory_display || 'tenant_name';
    const anyActivated = (unit.tenants ?? []).some((t) => !!t.activated || t.person_type === 'property_desk_user');
    if (mode === 'unit_only' || mode === 'display_name') {
      return [{ key: `${unit.id}`, unit, label: mode === 'display_name' ? (unit.display_name || null) : null, tenantActivated: anyActivated }];
    }
    const list = (unit.tenants ?? []).filter((t) => t.first_name || t.last_name);
    if (list.length === 0) {
      // Vacant or names not loaded — show the unit row with whatever
      // legacy label is on the unit.
      return [{ key: `${unit.id}`, unit, label: getUnitLabel(unit), tenantActivated: anyActivated }];
    }
    return list.map((t) => {
      // Honor per-tenant-per-property display prefs from the iOS app:
      //  • show_name === false  → hide the name (label = null, unit-only)
      //  • name_source 'custom' → use the custom override
      //  • else                 → default first+last from profile
      const prefs = t.display_prefs ?? null;
      const first = (t.first_name || '').trim();
      const last = (t.last_name || '').trim();
      const nameFormat = settings?.directory_name_format;
      const profileName = nameFormat === 'Last, First'
        ? [last, first].filter(Boolean).join(', ')
        : nameFormat === 'First Initial + Last'
          ? [`${first ? `${first[0]}.` : ''}`, last].filter(Boolean).join(' ')
          : [first, last].filter(Boolean).join(' ');
      let label: string | null = profileName;
      if (prefs?.show_name === false) {
        label = null;
      } else if (prefs?.name_source === 'custom' && prefs.custom_name && prefs.custom_name.trim()) {
        label = prefs.custom_name.trim();
      }
      return {
        key: `${unit.id}-${t.id}`,
        unit,
        label,
        tenantId: t.id,
        tenantActivated: !!t.activated || t.person_type === 'property_desk_user',
      };
    });
  };

  // ─── Theme ────────────────────────────────────────────────────────────────

  const style = settings?.display_style || 'modern_dark';
  const accent = settings?.accent_color || '#059669';

  const themes = {
    modern_dark: {
      // Matches the landing site's hero-gradient (.hero-gradient + .grid-pattern
      // in index.css) so every visitor intercom screen shares the same brand
      // background — emerald glows on `#101010` ink with a faint grid overlay.
      bg: 'hero-gradient grid-pattern',
      card: 'bg-white/10 backdrop-blur-sm border-white/10',
      text: 'text-white',
      textSecondary: 'text-gray-400',
      textMuted: 'text-gray-500',
      unitBg: 'bg-white/10',
      unitHover: 'hover:bg-white/20',
      unitActive: 'bg-emerald-600/30',
      searchBg: 'bg-white/10 text-white placeholder-gray-500 border-white/10',
      divider: 'border-white/10',
      floorLabel: 'text-gray-400',
      powered: 'text-gray-600',
      poweredBrand: 'text-gray-500',
    },
    modern_light: {
      bg: 'bg-gradient-to-br from-gray-50 via-white to-emerald-50',
      card: 'bg-white border-gray-200 shadow-sm',
      text: 'text-gray-900',
      textSecondary: 'text-gray-500',
      textMuted: 'text-gray-400',
      unitBg: 'bg-gray-100',
      unitHover: 'hover:bg-gray-200',
      unitActive: 'bg-emerald-100',
      searchBg: 'bg-white text-gray-900 placeholder-gray-400 border-gray-200',
      divider: 'border-gray-200',
      floorLabel: 'text-gray-500',
      powered: 'text-gray-400',
      poweredBrand: 'text-gray-500',
    },
    classic: {
      bg: 'bg-gradient-to-b from-stone-100 to-stone-200',
      card: 'bg-white border-stone-300 shadow-md',
      text: 'text-stone-900',
      textSecondary: 'text-stone-500',
      textMuted: 'text-stone-400',
      unitBg: 'bg-stone-100',
      unitHover: 'hover:bg-stone-200',
      unitActive: 'bg-emerald-100',
      searchBg: 'bg-white text-stone-900 placeholder-stone-400 border-stone-300',
      divider: 'border-stone-200',
      floorLabel: 'text-stone-500',
      powered: 'text-stone-400',
      poweredBrand: 'text-stone-500',
    },
  };

  const t = themes[style] || themes.modern_dark;
  // A saved color is an explicit branding choice, including black and the
  // editor's default #111827. Never silently substitute the hero gradient.
  const customBg = settings?.background_color || null;
  const fallbackWordmark = '/intrlock-wordmark-white.svg';
  const fallbackWordmarkStyle = style === 'modern_dark' ? undefined : { filter: 'brightness(0)' };


  // ─── Auto-lock: check if current time is within lock hours ───────────────

  const isAutoLocked = (): boolean => {
    if (!settings?.auto_lock_enabled) return false;
    const start = settings.auto_lock_start;
    const end = settings.auto_lock_end;
    if (!start || !end) return false;

    const now = new Date();
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    // Handle overnight ranges (e.g. 22:00 – 06:00)
    if (startMinutes <= endMinutes) {
      return nowMinutes >= startMinutes && nowMinutes < endMinutes;
    } else {
      return nowMinutes >= startMinutes || nowMinutes < endMinutes;
    }
  };

  const autoLocked = isAutoLocked();

  // ─── PIN visibility ───────────────────────────────────────────────────────

  // The shared keypad accepts tenant, guest, and delivery codes. Each method
  // is separately gated; an intercom cannot override a disabled property
  // entitlement (PIN and Face are premium property-level opt-ins).
  const showPinButton = showVisitorPinEntry(settings, {
    pin_access_enabled: settings?.pin_access_enabled,
    guest_pin_access_enabled: settings?.guest_pin_access_enabled,
  });

  // ─── PIN unlock handler ─────────────────────────────────────────────────

  // Capture a single still from the user's webcam. Returns null when the
  // browser has no camera, permission is denied, or the page isn't on
  // https (getUserMedia is gated to secure contexts). Resized to 480px
  // wide JPEG @ ~70% quality to keep the payload around 30-50 KB.
  const tryCapturePhoto = async (): Promise<string | null> => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return null;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      // Brief warm-up so the auto-exposure doesn't capture a black frame.
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      await video.play();
      await new Promise((r) => setTimeout(r, 400));
      const w = Math.min(480, video.videoWidth || 480);
      const h = Math.round((video.videoHeight || 360) * (w / (video.videoWidth || 640)));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { stream.getTracks().forEach((t) => t.stop()); return null; }
      ctx.drawImage(video, 0, 0, w, h);
      stream.getTracks().forEach((t) => t.stop());
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      return dataUrl.split(',')[1] || null;
    } catch {
      return null;
    }
  };

  const handlePinSubmit = async () => {
    if (pin.length < 4) return;
    setPinStatus('loading');
    setPinMessage('');
    try {
      const passWindow = (settings as any)?.visitor_code_response === 'grant_and_open_pass'
        ? window.open('', '_blank') : null;
      if (passWindow) passWindow.opener = null;
      try {
        const passResponse = await fetch(`${apiOrigin()}/visitors/passes/redeem-directory-code`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ property_id: property?.id, code: pin, intercom_config_id: intercomId || null }),
        });
        if (passResponse.ok) {
          const passResult = await passResponse.json();
          if (passResult.pass_url && passWindow) passWindow.location.href = passResult.pass_url;
          else passWindow?.close();
          setPinStatus('success');
          setPinMessage(passResult.pass_url ? 'Entry granted. Your Visitor Pass opened in a new window.' : 'Entry granted.');
          setTimeout(() => { setShowPinPad(false); setPin(''); setPinStatus('idle'); setPinMessage(''); }, 4000);
          return;
        }
        passWindow?.close();
        if (passResponse.status !== 404) throw new Error('Visitor pass verification failed');
      } catch (passError) {
        passWindow?.close();
        if (passError instanceof Error && passError.message === 'Visitor pass verification failed') throw passError;
        // Network failure falls through to the existing resident/legacy PIN route.
      }
      // Only request location when the property has geofence enabled. The
      // PM has not opted into location requirements unless this flag is on,
      // so visitor surfaces should never prompt for GPS otherwise.
      const geofenceOn = (property as any)?.geofence_enabled === true;
      const coords = await new Promise<{ lat: number | null; lng: number | null }>((resolve) => {
        if (!geofenceOn || !navigator.geolocation) return resolve({ lat: null, lng: null });
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve({ lat: null, lng: null }),
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
        );
      });
      // Capture a photo when the PM has enabled pin-entry photos for this
      // property/intercom. Camera is not asked otherwise.
      // Check the merged settings (intercom-config overrides applied on top
      // of the property) — when the PM saves the toggle on a single
      // intercom card, the property row stays unset but the settings
      // object reflects the override.
      const photoEnabled =
        (settings as any)?.pin_entry_photo_enabled === true
        || (property as any)?.pin_entry_photo_enabled === true;
      const photoRequired =
        (settings as any)?.pin_entry_photo_required === true
        || (property as any)?.pin_entry_photo_required === true;
      const entryPhoto = photoEnabled ? await tryCapturePhoto() : null;
      if (photoEnabled && photoRequired && !entryPhoto) {
        setPinStatus('error');
        setPinMessage('Camera access is required to use this PIN. Please allow camera access and try again.');
        return;
      }
      const response = await fetch(`${apiOrigin()}/intercom/unlock-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property?.id,
          pin,
          intercom_config_id: intercomId || null,
          user_lat: coords.lat,
          user_lng: coords.lng,
          ...(entryPhoto ? { entry_photo_base64: entryPhoto } : {}),
        }),
      });
      const data = await response.json();
      if (response.ok && data.unlocked) {
        const who = data.tenant_name || data.guest_name || 'Guest';
        setPinStatus('success');
        setPinMessage(`Welcome, ${who}! Door unlocked.`);
        if (data.pin_type === 'delivery' && data.delivery) {
          setDeliveryInfo({
            carrier: data.delivery.carrier || data.guest_name || 'Delivery',
            notes: data.delivery.notes || '',
            tenantName: data.delivery.tenant_name || '',
            doorMessage: data.message || 'Door unlocked.',
          });
          // Hold the delivery screen open — courier dismisses manually.
          setShowPinPad(false);
          setPin('');
        } else {
          setTimeout(() => {
            setShowPinPad(false);
            setPin('');
            setPinStatus('idle');
            setPinMessage('');
          }, 4000);
        }
      } else {
        setPinStatus('error');
        setPinMessage(data.error || data.message || 'Invalid PIN code');
        setTimeout(() => { setPinStatus('idle'); setPinMessage(''); }, 3000);
        setPin('');
      }
    } catch {
      setPinStatus('error');
      setPinMessage('Connection error. Please try again.');
      setTimeout(() => { setPinStatus('idle'); setPinMessage(''); }, 3000);
      setPin('');
    }
  };

  const handlePinKey = (key: string) => {
    if (pinStatus === 'loading' || pinStatus === 'success') return;
    if (key === 'clear') { setPin(''); setPinStatus('idle'); setPinMessage(''); return; }
    if (key === 'back') { setPin(p => p.slice(0, -1)); return; }
    if (pin.length >= 6) return;
    const newPin = pin + key;
    setPin(newPin);
    if (newPin.length >= 4) {
      // Auto-submit when 4-6 digits entered after a brief delay
      // User can keep typing up to 6 or it auto-submits at 6
    }
  };

  // ─── Face ID handlers ─────────────────────────────────────────────────────

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (livenessTimerRef.current) {
      clearInterval(livenessTimerRef.current);
      livenessTimerRef.current = null;
    }
  }, []);

  const closeFaceModal = useCallback(() => {
    stopCamera();
    setShowFaceModal(false);
    setFaceStatus('idle');
    setFaceMessage('');
    setFaceCountdown(3);
    setLivenessProgress(0);
  }, [stopCamera]);

  const startFaceCapture = useCallback(async () => {
    setFaceStatus('starting');
    setFaceMessage('');
    setFaceCountdown(3);
    setLivenessProgress(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;

      // Wait for videoRef to be ready
      await new Promise<void>((resolve) => {
        const check = () => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().then(resolve).catch(resolve);
          } else {
            setTimeout(check, 50);
          }
        };
        check();
      });

      setFaceStatus('liveness');

      // Camera warm-up only. This timer does NOT perform face detection or
      // AWS Face Liveness; never present it as an anti-spoofing check.
      let elapsed = 0;
      livenessTimerRef.current = setInterval(() => {
        elapsed += 100;
        setLivenessProgress(Math.min(100, (elapsed / 2000) * 100));
        if (elapsed >= 2000) {
          if (livenessTimerRef.current) clearInterval(livenessTimerRef.current);
          livenessTimerRef.current = null;
          // Start countdown then capture
          setFaceStatus('scanning');
          let count = 3;
          setFaceCountdown(count);
          const countTimer = setInterval(() => {
            count -= 1;
            setFaceCountdown(count);
            if (count <= 0) {
              clearInterval(countTimer);
              captureAndVerifyRef.current();
            }
          }, 1000);
        }
      }, 100);
    } catch (err: any) {
      console.error('Camera error:', err);
      setFaceStatus('error');
      setFaceMessage(
        err?.name === 'NotAllowedError'
          ? 'Camera access denied. Please allow camera access and try again.'
          : 'Could not access camera. Please try again.',
      );
    }
  }, []);

  const captureAndVerify = useCallback(async () => {
    setFaceStatus('capturing');

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setFaceStatus('error');
      setFaceMessage('Capture failed. Please try again.');
      return;
    }

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setFaceStatus('error');
      setFaceMessage('Capture failed. Please try again.');
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    // Strip the data URL prefix to get raw base64
    const image = imageDataUrl.replace(/^data:image\/jpeg;base64,/, '');

    stopCamera();
    setFaceStatus('verifying');

    try {
      const res = await fetch(`${FACE_API}/face/verify-kiosk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property?.id,
          image_base64: image,
          intercom_config_id: intercomId || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setFaceStatus('success');
        setFaceMessage(`Welcome${data.person_name ? `, ${data.person_name}` : ''}! Door unlocked.`);
        setTimeout(() => closeFaceModal(), 4000);
      } else {
        const msg =
          data.error === 'face_not_recognized' ? 'Face not recognized. Please try again.' :
          data.error === 'no_faces_enrolled' ? 'Face access is not set up for this property.' :
          data.error === 'access_disabled' ? 'Your face access has been disabled. Contact the property manager.' :
          data.detail || data.error || 'Face not recognized.';
        setFaceStatus('error');
        setFaceMessage(msg);
      }
    } catch {
      setFaceStatus('error');
      setFaceMessage('Connection error. Please try again.');
    }
  }, [property, stopCamera, closeFaceModal]);

  // Keep captureAndVerifyRef current
  useEffect(() => {
    captureAndVerifyRef.current = captureAndVerify;
  }, [captureAndVerify]);

  // Clean up camera on unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const showFaceButton = showVisitorFaceEntry(settings, { face_access_enabled: settings?.face_access_enabled });

  // ─── Loading state ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`} style={customBg ? { background: customBg, backgroundImage: 'none' } : undefined}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className={`${t.text} text-sm animate-pulse`}>Loading property...</p>
        </div>
      </div>
    );
  }

  // ─── Error state ──────────────────────────────────────────────────────────

  if (error || !property) {
    return (
      <div className="min-h-screen hero-gradient grid-pattern flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Property Not Found</h1>
          <p className="text-gray-400 text-sm">{error || 'This visitor page is not available.'}</p>
          <button
            onClick={() => { void loadData(); }}
            className="mt-6 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ─── Auto-lock screen ─────────────────────────────────────────────────────

  if (autoLocked) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center p-6`} style={customBg ? { background: customBg, backgroundImage: 'none' } : undefined}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className={`text-2xl font-bold ${t.text} mb-2`}>Directory Offline</h1>
          <p className={`${t.textSecondary} text-sm`}>
            Visitor access is unavailable during these hours.
            {settings?.auto_lock_start && settings?.auto_lock_end && (
              <> Please return between {settings.auto_lock_end} and {settings.auto_lock_start}.</>
            )}
          </p>
          {property && (
            <p className={`${t.textMuted} text-xs mt-4`}>{property.name}</p>
          )}
        </div>
      </div>
    );
  }

  // ─── Private directory: search-only mode ──────────────────────────────────

  if (settings?.private_directory) {
    return (
      <div className={`min-h-screen ${t.bg} flex flex-col`} style={customBg ? { background: customBg, backgroundImage: 'none' } : undefined}>
        <header className="pt-8 pb-4 px-6 text-center">
          {settings?.logo_url || property.logo_url ? (
            <img
              src={settings?.logo_url || property.logo_url}
              alt={property.name}
              className="rounded-2xl mx-auto mb-3 object-contain shadow-lg"
              style={{ width: `${settings?.logo_size_px ?? 80}px`, height: `${settings?.logo_size_px ?? 80}px` }}
            />
          ) : (
            <div className="inline-block mx-auto pb-[25px]">
              <img
                src={fallbackWordmark}
                alt="Intrlock"
                className="block h-8 w-auto object-contain mx-auto"
                style={fallbackWordmarkStyle}
              />
              <p className={`${t.text} mt-0.5 text-[7px] font-medium text-left pl-[39%] leading-none`}>cloud-based intercom</p>
            </div>
          )}
          <h1 className={`text-xl font-bold ${t.text}`}>{property.name}</h1>
          {intercomLabel && (
            <p className={`text-base font-medium ${t.text} opacity-80 mt-4`}>{intercomLabel}</p>
          )}
          <div
            className={`mt-5 mx-auto max-w-sm rounded-2xl border ${t.card} px-5 py-4 text-center`}
            style={{
              backgroundColor: `${accent}1F`,
              boxShadow: `0 6px 24px -10px ${accent}66`,
              borderColor: `${accent}55`,
            }}
          >
            <p className={`text-sm leading-relaxed ${t.text}`}>
              Enter a unit number to find the resident you are visiting.
            </p>
          </div>
        </header>

        <div className="px-6 mb-4">
          <div className="max-w-md mx-auto relative">
            <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.textMuted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Enter unit number..."
              className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${t.searchBg}`}
              style={{ '--tw-ring-color': accent } as any}
              autoFocus
            />
          </div>
        </div>

        <main className="flex-1 px-6 pb-8">
          <div className="max-w-md mx-auto">
            {search.trim() && filteredUnits.length === 0 && (
              <div className={`rounded-2xl border ${t.card} p-8 text-center`}>
                <p className={`${t.textSecondary} text-sm`}>No unit matching "{search}"</p>
              </div>
            )}
            {search.trim() && filteredUnits.length > 0 && (
              <div className={`rounded-2xl border overflow-hidden ${t.card}`}>
                <div className={`divide-y ${t.divider}`}>
                  {filteredUnits.map((unit) => (
                    <UnitRow
                      key={unit.id}
                      unit={unit}
                      label={getUnitLabel(unit)}
                      theme={t}
                      accent={accent}
                      allowVideoCall={
                        (settings?.allow_video_call ?? true) &&
                        (isKioskMode || (unit.tenants ?? []).some((tn) => !!tn.activated || tn.person_type === 'property_desk_user'))
                      }
                      allowPhoneCall={
                        intercomLoaded
                          ? !!intercomPhone && settings?.allow_phone_call !== false
                          : (settings?.allow_phone_call ?? false)
                      }
                      telnyxNumber={intercomLoaded ? (intercomPhone || undefined) : settings?.telnyx_number}
                      onCall={() => navigate(`${visitorBase()}/${propertyId}${intercomPathSegment}/call/${unit.unit_number}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <footer className="pb-6 text-center">
          {settings?.custom_footer_text && <p className={`text-xs ${t.powered}`}>{settings.custom_footer_text}</p>}
          <p className={`text-xs ${t.powered}`}>
            Powered by{' '}
            <a href="https://intrlock.app" className={`font-medium ${t.poweredBrand} hover:underline`}>Intrlock</a>
          </p>
        </footer>
      </div>
    );
  }

  // ─── Main page ────────────────────────────────────────────────────────────

  return (
    <div className={`min-h-screen ${t.bg} flex flex-col`} style={customBg ? { background: customBg, backgroundImage: 'none' } : undefined}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="pt-8 pb-4 px-6 text-center">
        {/* Property logo — when the property manager hasn't uploaded one, fall
            back to the Intrlock banner + "cloud-based intercom" tagline (same
            treatment as the login portal). */}
        {settings?.logo_url || property.logo_url ? (
          <img
            src={settings?.logo_url || property.logo_url}
            alt={property.name}
            className="rounded-2xl mx-auto mb-3 object-contain shadow-lg"
            style={{
              height: `${settings?.logo_size_px ?? 80}px`,
              width: `${settings?.logo_size_px ?? 80}px`,
            }}
          />
        ) : (
          <div className="inline-block mx-auto pb-[25px]">
            <img
            src={fallbackWordmark}
            alt="Intrlock"
            className="block h-10 w-auto object-contain mx-auto"
            style={fallbackWordmarkStyle}
            />
            <p className={`${t.text} mt-0.5 text-[8px] font-medium text-left pl-[39%] leading-none`}>cloud-based intercom</p>
          </div>
        )}

        <h1 className={`text-xl font-bold ${t.text}`}>{property.name}</h1>
        {intercomLabel && (
          <p className={`text-base font-medium ${t.text} opacity-80 mt-4`}>{intercomLabel}</p>
        )}

        {property.address && (
          <p className={`text-sm ${t.textSecondary} mt-1`}>
            {property.address}
            {property.city ? `, ${property.city}` : ''}
            {property.state ? `, ${property.state}` : ''}
            {property.zip ? ` ${property.zip}` : ''}
          </p>
        )}

        {/* Greeting message — promoted to a card so visitors actually read it */}
        <div
          className={`mt-5 mx-auto max-w-sm rounded-2xl border ${t.card} px-5 py-4 text-center`}
          style={{
            backgroundColor: `${accent}1F`,
            boxShadow: `0 6px 24px -10px ${accent}66`,
            borderColor: `${accent}55`,
          }}
        >
          <p className={`text-sm leading-relaxed ${t.text}`}>
            {settings?.greeting_message || 'Welcome! Please select the unit you\'re visiting.'}
          </p>
        </div>
      </header>

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <div className="px-6 mb-4">
        <div className="max-w-md mx-auto relative">
          <svg
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.textMuted}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by unit, name, or floor..."
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${t.searchBg}`}
            style={{ '--tw-ring-color': accent } as any}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className={`absolute right-3 top-1/2 -translate-y-1/2 ${t.textMuted} hover:${t.text}`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Access Buttons (PIN + Face ID) ─────────────────────────────────── */}
      {(showPinButton || showFaceButton) && (
        <div className="px-6 mb-4">
          <div className={`max-w-md mx-auto ${showPinButton && showFaceButton ? 'grid grid-cols-2 gap-3' : ''}`}>
            {showPinButton && (
              <button
                onClick={() => { setShowPinPad(true); setPin(''); setPinStatus('idle'); setPinMessage(''); }}
                className={`w-full py-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${t.card} ${t.text} ${t.unitHover}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Enter PIN to Unlock
              </button>
            )}
            {showFaceButton && (
              <button
                onClick={() => { setShowFaceModal(true); startFaceCapture(); }}
                className={`w-full py-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${t.card} ${t.text} ${t.unitHover}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A9 9 0 1118.88 6.196M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Face ID Access
              </button>
            )}
          </div>
        </div>
      )}

      {/* Leasing-office calls are property-scoped Desk Station calls. Keep
          this action beside the access methods so visitors do not have to
          search the resident directory for a synthetic office unit. */}
      {settings?.allow_video_call !== false && (
        <div className="px-6 mb-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => navigate(`${visitorBase()}/${propertyId}${intercomPathSegment}/call/leasing-office?desk=1`)}
              className={`w-full py-3 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${t.card} ${t.text} ${t.unitHover}`}
              style={{ borderColor: `${accent}88` }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.3a1 1 0 01.95.68l1.5 4.5a1 1 0 01-.5 1.2l-2.26 1.13a11 11 0 005.52 5.52l1.13-2.26a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.69.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
              </svg>
              Call Leasing Office
            </button>
          </div>
        </div>
      )}

      {/* ── Delivery Instructions Overlay ───────────────────────────────────── */}
      {deliveryInfo && (
        <div className="fixed inset-0 bg-gradient-to-b from-orange-50 to-amber-50 z-[60] flex flex-col items-center justify-center p-6 text-slate-900">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-orange-200 overflow-hidden">
            <div className="bg-orange-500 text-white p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                <svg className="w-9 h-9" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div className="text-sm uppercase tracking-wider opacity-90">Delivery Access Granted</div>
              <div className="text-2xl font-bold mt-1">{deliveryInfo.doorMessage}</div>
            </div>
            <div className="p-6 space-y-4">
              {deliveryInfo.tenantName && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">From</div>
                  <div className="text-lg font-semibold mt-1">{deliveryInfo.tenantName}</div>
                </div>
              )}
              {deliveryInfo.carrier && deliveryInfo.carrier !== 'Driver' && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Carrier</div>
                  <div className="text-base mt-1">{deliveryInfo.carrier}</div>
                </div>
              )}
              {deliveryInfo.notes ? (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Delivery Instructions</div>
                  <div className="mt-2 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-base text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {deliveryInfo.notes}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 italic">No special instructions from tenant.</div>
              )}
            </div>
            <button
              onClick={() => {
                setDeliveryInfo(null);
                setPinStatus('idle');
                setPinMessage('');
              }}
              className="w-full p-4 bg-slate-100 hover:bg-slate-200 transition-colors text-slate-900 font-semibold text-base border-t border-slate-200"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── PIN Pad Modal ───────────────────────────────────────────────────── */}
      {showPinPad && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-slate-900 rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 text-center border-b border-white/10">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accent }}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-white">Enter Your PIN</h2>
              <p className="text-sm text-gray-400 mt-1">Enter your access PIN to unlock the door</p>
            </div>

            {/* PIN Display */}
            <div className="px-6 py-5">
              <div className="flex justify-center gap-3 mb-2">
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div
                    key={i}
                    className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all ${
                      pin.length > i
                        ? 'border-white/40 bg-white/10 text-white'
                        : i < 4
                          ? 'border-white/20 bg-white/5 text-transparent'
                          : 'border-white/10 bg-transparent text-transparent'
                    }`}
                  >
                    {pin[i] ? '*' : ''}
                  </div>
                ))}
              </div>

              {/* Status message */}
              {pinMessage && (
                <div className={`text-center text-sm mt-3 font-medium ${
                  pinStatus === 'success' ? 'text-green-400' : pinStatus === 'error' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {pinStatus === 'success' && (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {pinMessage}
                    </div>
                  )}
                  {pinStatus === 'error' && pinMessage}
                </div>
              )}
              {pinStatus === 'loading' && (
                <div className="text-center mt-3">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto" />
                </div>
              )}
            </div>

            {/* Keypad */}
            <div className="px-6 pb-4">
              <div className="grid grid-cols-3 gap-3">
                {['1','2','3','4','5','6','7','8','9','clear','0','back'].map(key => (
                  <button
                    key={key}
                    onClick={() => key === 'clear' || key === 'back' ? handlePinKey(key) : handlePinKey(key)}
                    disabled={pinStatus === 'loading' || pinStatus === 'success'}
                    className={`h-14 rounded-xl text-lg font-semibold transition-all ${
                      key === 'clear'
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm'
                        : key === 'back'
                          ? 'bg-white/10 text-gray-400 hover:bg-white/20'
                          : 'bg-white/10 text-white hover:bg-white/20 active:bg-white/30'
                    } disabled:opacity-40`}
                  >
                    {key === 'clear' ? 'Clear' : key === 'back' ? (
                      <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414-6.414a2 2 0 011.414-.586H19a2 2 0 012 2v10a2 2 0 01-2 2h-8.172a2 2 0 01-1.414-.586L3 12z" />
                      </svg>
                    ) : key}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => { setShowPinPad(false); setPin(''); setPinStatus('idle'); setPinMessage(''); }}
                className="flex-1 py-3 rounded-xl bg-white/10 text-gray-300 font-medium text-sm hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <DeviceActionButton
                surface="touch"
                onClick={handlePinSubmit}
                disabled={pin.length < 4 || pinStatus === 'loading' || pinStatus === 'success'}
                loading={pinStatus === 'loading'}
                loadingLabel="Unlocking…"
                className="flex-1"
              >
                Unlock
              </DeviceActionButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Face ID Modal ───────────────────────────────────────────────────── */}
      {showFaceModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-slate-900 rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 text-center border-b border-white/10">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accent }}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A9 9 0 1118.88 6.196M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-white">Face Access</h2>
              <p className="text-sm text-gray-400 mt-1">
                {faceStatus === 'idle' || faceStatus === 'starting'
                  ? 'Starting camera...'
                  : faceStatus === 'liveness'
                  ? 'Preparing camera...'
                  : faceStatus === 'scanning'
                  ? 'Position your face in the frame'
                  : faceStatus === 'capturing'
                  ? 'Capturing image...'
                  : faceStatus === 'verifying'
                  ? 'Verifying identity...'
                  : faceStatus === 'success'
                  ? 'Identity confirmed!'
                  : 'Face access'}
              </p>
            </div>

            {/* Camera / Status Area */}
            <div className="px-6 py-5">
              {/* Video feed is mounted while the camera prepares. */}
              <div
                className="relative mx-auto"
                style={{
                  width: 220, height: 220,
                  display: (faceStatus === 'starting' || faceStatus === 'liveness' || faceStatus === 'scanning' || faceStatus === 'capturing') ? 'block' : 'none',
                  visibility: faceStatus === 'starting' ? 'hidden' : 'visible',
                }}
              >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded-2xl"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  {/* Face oval overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div
                      className="border-4 rounded-full"
                      style={{
                        width: 140,
                        height: 180,
                        borderColor: faceStatus === 'scanning'
                          ? `${accent}`
                          : faceStatus === 'liveness'
                          ? 'rgba(255,255,255,0.4)'
                          : 'rgba(255,255,255,0.2)',
                        boxShadow: faceStatus === 'scanning' ? `0 0 20px ${accent}60` : 'none',
                        transition: 'border-color 0.3s, box-shadow 0.3s',
                      }}
                    />
                  </div>
                  {/* Countdown badge */}
                  {faceStatus === 'scanning' && faceCountdown > 0 && (
                    <div
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg"
                      style={{ backgroundColor: accent }}
                    >
                      {faceCountdown}
                    </div>
                  )}
                </div>

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Camera preparation progress; not a liveness result. */}
              {faceStatus === 'liveness' && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Preparing camera...</span>
                    <span>{Math.round(livenessProgress)}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-100"
                      style={{ width: `${livenessProgress}%`, backgroundColor: accent }}
                    />
                  </div>
                </div>
              )}

              {/* Starting / verifying spinner */}
              {(faceStatus === 'starting' || faceStatus === 'verifying' || faceStatus === 'capturing') && (
                <div className="flex flex-col items-center py-6 gap-3">
                  <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span className="text-sm text-gray-400">
                    {faceStatus === 'starting' ? 'Starting camera...' : faceStatus === 'capturing' ? 'Capturing...' : 'Verifying...'}
                  </span>
                </div>
              )}

              {/* Success state */}
              {faceStatus === 'success' && (
                <div className="flex flex-col items-center py-6 gap-3">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <svg className="w-9 h-9 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-green-400 text-sm font-medium text-center">{faceMessage}</p>
                </div>
              )}

              {/* Error state */}
              {faceStatus === 'error' && (
                <div className="flex flex-col items-center py-5 gap-3">
                  <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-red-400 text-sm font-medium text-center">{faceMessage || 'Face not recognized.'}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={closeFaceModal}
                className="flex-1 py-3 rounded-xl bg-white/10 text-gray-300 font-medium text-sm hover:bg-white/20 transition-colors"
              >
                {faceStatus === 'success' ? 'Close' : 'Cancel'}
              </button>
              {faceStatus === 'error' && (
                <button
                  onClick={() => startFaceCapture()}
                  className="flex-1 py-3 rounded-xl text-white font-medium text-sm transition-colors"
                  style={{ backgroundColor: accent }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Unit count ─────────────────────────────────────────────────────── */}
      <div className="px-6 mb-2">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <span className={`text-xs uppercase tracking-wider ${t.textMuted}`}>
            {settings?.show_tenant_count !== false && (
              <>{filteredUnits.reduce((count, unit) => count + (unit.tenants?.length || 0), 0)} Tenant{filteredUnits.reduce((count, unit) => count + (unit.tenants?.length || 0), 0) === 1 ? '' : 's'}</>
            )}
          </span>
          {floors.length > 1 && (
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('directory')}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === 'directory' ? `${t.unitActive} ${t.text}` : `${t.textMuted}`
                }`}
              >
                By Floor
              </button>
              <button
                onClick={() => setViewMode('search')}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === 'search' ? `${t.unitActive} ${t.text}` : `${t.textMuted}`
                }`}
              >
                All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Unit Directory ─────────────────────────────────────────────────── */}
      <main className="flex-1 px-6 pb-8">
        <div className="max-w-md mx-auto">
          {filteredUnits.length === 0 ? (
            <div className={`rounded-2xl border ${t.card} p-8 text-center`}>
              <svg
                className={`w-12 h-12 mx-auto mb-3 ${t.textMuted}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className={`${t.textSecondary} text-sm`}>
                {search ? `No units matching "${search}"` : 'No units available'}
              </p>
            </div>
          ) : viewMode === 'directory' && floors.length > 1 && (settings?.show_floor_grouping ?? true) ? (
            // Grouped by floor
            <div className="space-y-4">
              {floors.map((floor) => (
                <div key={floor} className={`rounded-2xl border overflow-hidden ${t.card}`}>
                  <div className={`px-4 py-2.5 border-b ${t.divider}`}>
                    <span className={`text-xs uppercase tracking-wider font-medium ${t.floorLabel}`}>
                      Floor {floor}
                    </span>
                  </div>
                  <div className={`divide-y ${t.divider}`}>
                    {unitsByFloor[floor].flatMap((unit) =>
                      expandUnit(unit).map((entry) => (
                        <UnitRow
                          key={entry.key}
                          unit={entry.unit}
                          label={entry.label}
                          theme={t}
                          accent={accent}
                          // Per-tenant gating — video button only shows when
                          // this specific tenant has activated the mobile app.
                          allowVideoCall={(settings?.allow_video_call ?? true) && (isKioskMode || entry.tenantActivated)}
                          allowPhoneCall={intercomLoaded ? !!intercomPhone && settings?.allow_phone_call !== false : (settings?.allow_phone_call ?? false)}
                          telnyxNumber={intercomLoaded ? (intercomPhone || undefined) : settings?.telnyx_number}
                          onCall={() => {
                            const tQ = entry.tenantId ? `?tenant=${encodeURIComponent(entry.tenantId)}` : '';
                            navigate(`${visitorBase()}/${propertyId}${intercomPathSegment}/call/${entry.unit.unit_number}${tQ}`);
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Flat list
            <div className={`rounded-2xl border overflow-hidden ${t.card}`}>
              <div className={`px-4 py-2.5 border-b ${t.divider}`}>
                <span className={`text-xs uppercase tracking-wider font-medium ${t.floorLabel}`}>
                  Unit Directory
                </span>
              </div>
              <div className={`divide-y ${t.divider}`}>
                {filteredUnits.flatMap((unit) =>
                  expandUnit(unit).map((entry) => (
                    <UnitRow
                      key={entry.key}
                      unit={entry.unit}
                      label={entry.label}
                      theme={t}
                      accent={accent}
                      // Per-tenant gating — see other UnitRow caller for rationale.
                      allowVideoCall={(settings?.allow_video_call ?? true) && (isKioskMode || entry.tenantActivated)}
                      // Match the "By Floor" branch — when on an intercom-specific
                      // URL, prefer the intercom's own phone number (it can differ
                      // from the property-level telnyx_number). Without this the
                      // flat "All" view drops the voice button whenever the phone
                      // lives on the intercom config instead of the property.
                      allowPhoneCall={intercomLoaded ? !!intercomPhone && settings?.allow_phone_call !== false : (settings?.allow_phone_call ?? false)}
                      telnyxNumber={intercomLoaded ? (intercomPhone || undefined) : settings?.telnyx_number}
                      onCall={() => {
                        const tQ = entry.tenantId ? `?tenant=${encodeURIComponent(entry.tenantId)}` : '';
                        navigate(`${visitorBase()}/${propertyId}${intercomPathSegment}/call/${entry.unit.unit_number}${tQ}`);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Property Contacts (opt-in via "Show in Visitor Directory") ─ */}
          {!isKioskMode && (() => {
            const raw = (property as any).contacts_json;
            const list: any[] = Array.isArray(raw)
              ? raw
              : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw) || []; } catch { return []; } })() : []);
            const visible = list.filter((c) => c && c.show_in_directory && (c.phone || c.email));
            if (!visible.length) return null;
            return (
              <div className={`rounded-2xl border overflow-hidden ${t.card} mt-4`}>
                <div className={`px-4 py-2.5 border-b ${t.divider}`}>
                  <span className={`text-xs uppercase tracking-wider font-medium ${t.floorLabel}`}>
                    Property Contacts
                  </span>
                </div>
                <div className={`divide-y ${t.divider}`}>
                  {visible.map((c, i) => (
                    <div key={c.id ?? i} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${t.text} truncate`}>{c.name}</p>
                        {c.role && <p className={`text-xs ${t.textMuted} truncate`}>{c.role}</p>}
                      </div>
                      {c.phone ? (
                        <a
                          href={`tel:${String(c.phone).replace(/[^+0-9]/g, '')}`}
                          className="text-sm font-medium px-3 py-1.5 rounded-lg"
                          style={{ backgroundColor: accent, color: 'white' }}
                        >
                          Call
                        </a>
                      ) : c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          className="text-sm font-medium hover:underline"
                          style={{ color: accent }}
                        >
                          Email
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </main>

      {/* ── Phone number ───────────────────────────────────────────────────── */}
      {!isKioskMode && settings?.show_phone && settings?.show_phone_on_directory !== false && property.phone && (
        <div className="text-center pb-4">
          <p className={`text-xs ${t.textMuted} mb-1`}>Need help? Call the front desk</p>
          <a
            href={`tel:${property.phone}`}
            className="text-sm font-medium hover:underline"
            style={{ color: accent }}
          >
            {property.phone}
          </a>
        </div>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="pb-6 text-center">
        {settings?.custom_footer_text && <p className={`text-xs ${t.powered}`}>{settings.custom_footer_text}</p>}
        <p className={`text-xs ${t.powered}`}>
          Powered by{' '}
          <a href="https://intrlock.app" className={`font-medium ${t.poweredBrand} hover:underline`}>Intrlock</a>
        </p>
      </footer>
    </div>
  );
};

// ─── Unit Row Component ───────────────────────────────────────────────────────

interface UnitRowProps {
  unit: VisitorUnit;
  label: string | null;
  theme: any;
  accent: string;
  allowVideoCall: boolean;
  allowPhoneCall: boolean;
  telnyxNumber?: string;
  onCall: () => void;
}

const UnitRow: React.FC<UnitRowProps> = ({ unit, label, theme: t, accent, allowVideoCall, allowPhoneCall, telnyxNumber, onCall }) => {
  // Build the tel: link with a DTMF pause + unit number + #. Each comma
  // in a tel: URI is ~2s on iOS, applied AFTER the call is answered. One
  // comma was empirically too short — Telnyx's <Gather> sometimes drops
  // the first 1-2 tones, so digits arrive as e.g. "99" or "9" and the
  // unit lookup fails. Two commas (~4s) reliably lands DTMF inside the
  // active gather window across carrier setup timings.
  // Letter-bearing units (B1, 101A, PH-2) can't be DTMF'd by iOS — it
  // strips alpha chars from post-dial DTMF. Use `dial_code` (the numeric
  // proxy stored on the unit, e.g. "1011" for "101A") when it's set, and
  // fall back to `unit_number` for purely numeric units that don't need
  // a proxy.
  const dialDigits = String(unit.dial_code || unit.unit_number || '').trim();
  const phoneHref = telnyxNumber && allowPhoneCall && dialDigits
    ? `tel:${telnyxNumber},,${dialDigits}%23`
    : undefined;

  return (
    <div className={`flex items-center justify-between px-4 py-3 ${t.unitHover} transition-all group`}>
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}20` }}
        >
          <span className={`text-sm font-bold ${t.text}`}>{unit.unit_number}</span>
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium ${t.text}`}>Unit {unit.unit_number}</p>
          {label && (
            <p className={`text-xs ${t.textSecondary} truncate`}>{label}</p>
          )}
        </div>
      </div>

      {/* Call buttons */}
      <div className="grid w-40 shrink-0 grid-cols-2 items-center gap-2">
        {/* Phone call button */}
        {allowPhoneCall && telnyxNumber && (
          <a
            href={phoneHref}
            className="box-border flex h-8 min-h-0 w-full min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-white text-xs font-medium opacity-70 group-hover:opacity-100 transition-all"
            style={{ backgroundColor: '#059669', height: 32, minHeight: 32, width: '100%', boxSizing: 'border-box' }}
            title={`Phone Unit ${unit.unit_number}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Phone
          </a>
        )}

        {/* Video call button */}
        {allowVideoCall && (
          <button
            onClick={onCall}
            className="box-border flex h-8 min-h-0 w-full min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-white text-xs font-medium opacity-70 group-hover:opacity-100 transition-all"
            style={{ backgroundColor: accent, height: 32, minHeight: 32, width: '100%', boxSizing: 'border-box' }}
            title={`Video Call Unit ${unit.unit_number}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Video
          </button>
        )}
      </div>
    </div>
  );
};

export default VisitorDirectory;
